/**
 * Chi tiết vụ án (nội bộ) – Thư ký: checklist, trả đơn, yêu cầu bổ sung; Chánh án: phân công Thẩm phán.
 */
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Alert,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Paper,
} from '@mui/material'
import { ArrowBack, CheckCircle, Cancel, Person, AutoAwesome, Description, Gavel, Event, Speed, Send } from '@mui/icons-material'
import { PageDataState } from '../../components/PageDataState'
import { useGetCaseByIdQuery, useAssignJudgeMutation } from '../../store/api/casesApi'
import { useGetFilingsByCaseQuery } from '../../store/api/filingsApi'
import { useGetJudgesQuery } from '../../store/api/judgesApi'
import type { CaseEvent } from '../../types/case'
import { useState } from 'react'
import { useAppSelector } from '../../hooks/redux'
import { buildFullTimeline, type TimelineDisplayItem } from '../../utils/caseTimeline'

/** Mock: AI đánh giá đủ điều kiện thủ tục rút gọn theo giá trị và loại vụ. */
function getRutGonSuggestion(claimValueVnd: number, category: string): { eligible: boolean; reason: string } {
  const under500M = claimValueVnd < 500_000_000
  const simpleCategory = ['marriage_family', 'civil'].includes(category)
  if (under500M && simpleCategory) {
    return { eligible: true, reason: 'Giá trị tranh chấp dưới 500 triệu VNĐ, vụ việc đơn giản. Đủ điều kiện xét xử theo thủ tục rút gọn (Điều 317 BLTTDS 2015).' }
  }
  if (!under500M) {
    return { eligible: false, reason: `Giá trị tranh chấp ${(claimValueVnd / 1e6).toFixed(0)} triệu VNĐ (≥ 500 triệu). Không đủ điều kiện thủ tục rút gọn.` }
  }
  return { eligible: false, reason: 'Loại vụ việc cần xét xét thêm. Có thể tham khảo ý kiện Chánh án.' }
}

function WorkspaceCaseDetailPage() {
  const { caseId } = useParams<{ caseId: string }>()
  const navigate = useNavigate()
  const user = useAppSelector((s) => s.auth.user)
  const [selectedJudge, setSelectedJudge] = useState('judge-1')
  const [checklist, setChecklist] = useState<Record<string, boolean>>({
    form_ok: false,
    parties_ok: false,
    content_ok: false,
    evidence_ok: false,
  })
  const { data: caseDetail, isLoading, error, refetch } = useGetCaseByIdQuery(caseId!, { skip: !caseId })
  const { data: filings = [] } = useGetFilingsByCaseQuery(caseId!, { skip: !caseId })
  const { data: judges = [] } = useGetJudgesQuery()
  const [assignJudge, { isLoading: assigning }] = useAssignJudgeMutation()

  const handleAssign = async () => {
    if (!caseId || !selectedJudge) return
    try {
      await assignJudge({ case_id: caseId, judge_id: selectedJudge }).unwrap()
      refetch()
    } catch (e) {
      console.error(e)
    }
  }

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/workspace/cases')} sx={{ mb: 2 }}>
        Quay lại
      </Button>

      <PageDataState isLoading={isLoading} error={error} empty={!caseDetail && !isLoading} onRetry={() => refetch()}>
        {caseDetail && (
          <>
            <Card sx={{ mb: 3, borderRadius: 3, boxShadow: 2 }}>
              <CardContent>
                <Typography variant="h5" fontWeight={600} gutterBottom>
                  {caseDetail.case_code}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Chip label={caseDetail.status} size="small" color="primary" />
                  <Chip label={caseDetail.track === 'A' ? 'Online' : 'Hybrid'} variant="outlined" size="small" />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Nguyên đơn: {caseDetail.plaintiff_name ?? caseDetail.plaintiff_id} · Bị đơn: {caseDetail.defendant_name ?? caseDetail.defendant_id}
                </Typography>
              </CardContent>
            </Card>

            {/* Bước 3 – AI: Kiểm tra điều kiện thủ tục rút gọn */}
            <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 1, borderLeft: 4, borderLeftColor: 'info.main' }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Speed fontSize="small" color="info" /> Điều kiện thủ tục rút gọn (AI)
                </Typography>
                {(() => {
                  const { eligible, reason } = getRutGonSuggestion(caseDetail.claim_value_vnd ?? 0, caseDetail.category ?? '')
                  return (
                    <>
                      <Alert severity={eligible ? 'success' : 'info'} icon={<AutoAwesome />} sx={{ py: 0.5 }}>
                        <Typography variant="body2">
                          <strong>Kết quả kiểm tra:</strong> {eligible ? 'Đủ điều kiện áp dụng thủ tục rút gọn.' : 'Chưa đủ điều kiện thủ tục rút gọn.'}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>{reason}</Typography>
                      </Alert>
                    </>
                  )
                })()}
              </CardContent>
            </Card>

            {/* Timeline tiến trình vụ án đầy đủ 8 bước – Thư ký / Thẩm phán / Chánh án */}
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Event fontSize="small" /> Tiến trình vụ án
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Toàn bộ các bước từ nộp đơn đến kết thúc vụ án. Các bước chưa diễn ra hiển thị là « Chưa diễn ra ».
            </Typography>
            <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 1 }}>
              <CardContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                  {buildFullTimeline(caseDetail.timeline ?? []).map((ev: TimelineDisplayItem) => (
                    <Box
                      key={ev.id}
                      sx={{
                        display: 'flex',
                        gap: 2,
                        pb: 2,
                        borderLeft: '2px solid',
                        borderColor: ev.isPending ? 'grey.300' : 'primary.main',
                        ml: 1.5,
                        pl: 3,
                        position: 'relative',
                        opacity: ev.isPending ? 0.85 : 1,
                        '&:last-of-type': { pb: 0 },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: -6,
                          top: 4,
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: ev.isPending ? 'grey.400' : 'primary.main',
                        },
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography fontWeight={600} variant="body2" color={ev.isPending ? 'text.secondary' : 'text.primary'}>
                          {ev.title}
                        </Typography>
                        {ev.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{ev.description}</Typography>
                        )}
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          {ev.isPending ? 'Chưa diễn ra' : `${new Date(ev.at).toLocaleString('vi-VN')}${ev.user_name ? ` · ${ev.user_name}` : ''}`}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>

            {/* Thư ký: Đơn + Checklist */}
            {filings.length > 0 && (
              <Card sx={{ mb: 3, borderRadius: 3, boxShadow: 2 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Đơn khởi kiện & Checklist kiểm tra hình thức
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                    <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Nội dung đơn</Typography>
                      {filings.map((f) => (
                        <Box key={f.id} sx={{ mb: 2 }}>
                          <Typography variant="body2"><strong>Loại:</strong> {f.type}</Typography>
                          <Typography variant="body2"><strong>Trạng thái:</strong> {f.status}</Typography>
                          {f.form_data && typeof f.form_data === 'object' && (
                            <List dense>
                              {Object.entries(f.form_data).map(([k, v]) => (
                                <ListItem key={k} dense><ListItemText primary={`${k}: ${String(v)}`} /></ListItem>
                              ))}
                            </List>
                          )}
                        </Box>
                      ))}
                    </Box>
                    <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Checklist kiểm tra hình thức</Typography>
                      <FormGroup sx={{ mb: 2 }}>
                        <FormControlLabel
                          control={<Checkbox checked={checklist.form_ok} onChange={(e) => setChecklist((c) => ({ ...c, form_ok: e.target.checked }))} />}
                          label="Đơn đúng mẫu, đủ chữ ký"
                        />
                        <FormControlLabel
                          control={<Checkbox checked={checklist.parties_ok} onChange={(e) => setChecklist((c) => ({ ...c, parties_ok: e.target.checked }))} />}
                          label="Đủ thông tin nguyên đơn, bị đơn"
                        />
                        <FormControlLabel
                          control={<Checkbox checked={checklist.content_ok} onChange={(e) => setChecklist((c) => ({ ...c, content_ok: e.target.checked }))} />}
                          label="Có nội dung yêu cầu rõ ràng"
                        />
                        <FormControlLabel
                          control={<Checkbox checked={checklist.evidence_ok} onChange={(e) => setChecklist((c) => ({ ...c, evidence_ok: e.target.checked }))} />}
                          label="Chứng cứ đính kèm (nếu có) đầy đủ"
                        />
                      </FormGroup>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button size="small" variant="outlined" startIcon={<CheckCircle />} color="success" disabled={!(checklist.form_ok && checklist.parties_ok && checklist.content_ok)}>
                          Chấp nhận thụ lý
                        </Button>
                        <Button size="small" variant="outlined" startIcon={<Cancel />} color="warning">
                          Đề xuất trả đơn
                        </Button>
                        <Button size="small" variant="outlined" startIcon={<Cancel />}>
                          Yêu cầu bổ sung
                        </Button>
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Description /> Dự thảo văn bản (tự động tạo)
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="subtitle2" fontWeight={600} gutterBottom>Thông báo nộp tiền tạm ứng án phí</Typography>
                      <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: '0.8125rem' }}>
{`TÒA ÁN NHÂN DÂN ...
Vụ án: [Mã vụ án] – [Loại tranh chấp]

Căn cứ đơn khởi kiện và tài liệu đính kèm, Tòa án thông báo nguyên đơn nộp tiền tạm ứng án phí theo quy định ...
Hạn nộp: trong 07 ngày làm việc kể từ ngày nhận thông báo này.

(Dự thảo do hệ thống tạo – Thư ký duyệt và gửi.)`}
                      </Typography>
                    </Paper>
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Chánh án: Phân công Thẩm phán */}
            {!caseDetail.assigned_judge_id && judges.length > 0 && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Person /> Phân công Thẩm phán
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Chọn Thẩm phán để phân công vụ án. Hệ thống gợi ý theo năng suất và chuyên môn.
                  </Typography>
                  {judges.length > 0 && (
                    <Alert severity="info" icon={<AutoAwesome />} sx={{ mb: 2 }}>
                      <strong>Gợi ý AI:</strong> {judges[0].name} – ít án đang xử lý ({judges[0].workload} vụ), phù hợp chuyên môn Hôn nhân & Gia đình.
                    </Alert>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <FormControl sx={{ minWidth: 260 }}>
                      <InputLabel>Thẩm phán</InputLabel>
                      <Select value={selectedJudge} label="Thẩm phán" onChange={(e) => setSelectedJudge(e.target.value)}>
                        {judges.map((j) => (
                          <MenuItem key={j.id} value={j.id}>
                            {j.name} (số vụ: {j.workload})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button variant="contained" onClick={handleAssign} disabled={!selectedJudge || assigning}>
                      {assigning ? 'Đang phân công...' : 'Phân công'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            )}

            {caseDetail.assigned_judge_name && (
              <Alert severity="success">Đã phân công: {caseDetail.assigned_judge_name}</Alert>
            )}

            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Gavel /> Legal Research – Gợi ý điều luật, án lệ
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  AI gợi ý các quy định pháp luật và án lệ liên quan đến tình tiết vụ việc; Thẩm phán có thể mở Công cụ AI Research để tra cứu sâu hơn.
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="body2" component="div">
                    <strong>Luật áp dụng:</strong> Luật Hôn nhân và gia đình 2014 (Điều 51, 56); Nghị quyết 02/2020/NQ-HĐTP.
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
                    <strong>Án lệ tham khảo:</strong> Án lệ 01/2016/AL – Giải quyết tranh chấp ly hôn khi có con nhỏ.
                  </Typography>
                </Paper>
              </CardContent>
            </Card>

            {/* Bước 8 – Thẩm phán: Gửi yêu cầu bổ sung chứng cứ */}
            {(user?.role === 'judge') && (
              <Card sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Send /> Hướng dẫn tố tụng & Thu thập chứng cứ
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Gửi yêu cầu bổ sung chứng cứ đến Đương sự qua hệ thống. Đương sự sẽ nhận thông báo và giao nộp tại mục Giao nộp chứng cứ.
                  </Typography>
                  <Button variant="outlined" startIcon={<Send />} onClick={() => navigate('/workspace/ai-evidence')}>
                    Gửi yêu cầu bổ sung chứng cứ (mock)
                  </Button>
                  <Button variant="text" size="small" sx={{ ml: 1 }} onClick={() => navigate('/workspace/ai-evidence')}>
                    Mở AI Chứng cứ
                  </Button>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </PageDataState>
    </Box>
  )
}

export default WorkspaceCaseDetailPage
